import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Terminal, Search, RefreshCw, AlertTriangle, Info, Bug, X } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/utils/cn'
import { formatDateTime } from '@/utils/format-date'
import api from '@/services/api'

type LogLevel = 'info' | 'warn' | 'error' | 'debug'
type LogCategory = 'system' | 'auth' | 'api' | 'database' | 'email' | 'payment'

interface LogEntry {
  id: string
  level: LogLevel
  category: LogCategory
  message: string
  details?: string
  userId?: string
  userName?: string
  ip?: string
  createdAt: string
}

const levelConfig: Record<LogLevel, { label: string; color: string; icon: React.ReactNode }> = {
  info: { label: 'INFO', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', icon: <Info size={12} /> },
  warn: { label: 'WARN', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', icon: <AlertTriangle size={12} /> },
  error: { label: 'ERROR', color: 'text-red-400 bg-red-500/10 border-red-500/20', icon: <AlertTriangle size={12} /> },
  debug: { label: 'DEBUG', color: 'text-purple-400 bg-purple-500/10 border-purple-500/20', icon: <Bug size={12} /> },
}

const categoryLabels: Record<LogCategory, string> = {
  system: 'Sistema',
  auth: 'Autenticação',
  api: 'API',
  database: 'Banco de Dados',
  email: 'Email',
  payment: 'Pagamento',
}

function normalizeLogEntry(raw: Partial<LogEntry> & { id: string; message: string }): LogEntry | null {
  const level = raw.level
  const category = raw.category
  if (!level || !levelConfig[level as LogLevel] || !category || !categoryLabels[category as LogCategory]) {
    return null
  }

  return {
    id: raw.id,
    level: level as LogLevel,
    category: category as LogCategory,
    message: raw.message,
    details: raw.details,
    userId: raw.userId,
    userName: raw.userName,
    ip: raw.ip,
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date(raw.createdAt as unknown as string).toISOString(),
  }
}

export function SystemLogsTab() {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'all'>('all')
  const [categoryFilter, setCategoryFilter] = useState<LogCategory | 'all'>('all')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/system/logs', {
        params: {
          level: levelFilter !== 'all' ? levelFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          search: debouncedSearch || undefined,
        },
      })

      const rows = Array.isArray(data.data) ? data.data : []
      const normalized = rows
        .map((row: Partial<LogEntry> & { id?: string; message?: string }) =>
          row?.id && row?.message ? normalizeLogEntry(row as LogEntry) : null,
        )
        .filter((row: LogEntry | null): row is LogEntry => row !== null)

      setLogs(normalized)
    } catch {
      setLogs([])
      toast.error('Não foi possível carregar os logs do sistema')
    } finally {
      setLoading(false)
    }
  }, [levelFilter, categoryFilter, debouncedSearch])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    if (!autoRefresh) return
    const interval = setInterval(fetchLogs, 15000)
    return () => clearInterval(interval)
  }, [autoRefresh, fetchLogs])

  const handleClearLogs = async () => {
    if (!window.confirm('Limpar logs de erro do cliente e eventos de segurança? Os logs de auditoria serão mantidos.')) {
      return
    }

    try {
      await api.delete('/system/logs')
      setLogs([])
      toast.success('Logs limpos com sucesso')
      await fetchLogs()
    } catch {
      toast.error('Erro ao limpar logs')
    }
  }

  const logLevels: (LogLevel | 'all')[] = ['all', 'info', 'warn', 'error', 'debug']
  const logCategories: (LogCategory | 'all')[] = ['all', 'system', 'auth', 'api', 'database', 'email', 'payment']

  const filteredLogs = useMemo(() => {
    const q = debouncedSearch.toLowerCase()
    return logs.filter((log) => {
      if (levelFilter !== 'all' && log.level !== levelFilter) return false
      if (categoryFilter !== 'all' && log.category !== categoryFilter) return false
      if (q && !log.message.toLowerCase().includes(q) && !(log.details ?? '').toLowerCase().includes(q)) {
        return false
      }
      return true
    })
  }, [logs, levelFilter, categoryFilter, debouncedSearch])

  return (
    <div className='space-y-5'>
      <div className='glass-card p-5 space-y-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Terminal size={18} className='text-[var(--brand-primary)]' />
            <h3 className='text-sm font-semibold text-[var(--font-color-h3)]'>Logs do Sistema</h3>
            <Badge variant='muted'>{filteredLogs.length} eventos</Badge>
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='ghost'
              size='sm'
              icon={<RefreshCw size={13} />}
              onClick={fetchLogs}
              loading={loading}
            >
              Atualizar
            </Button>
            <Button variant='destructive' size='sm' icon={<X size={13} />} onClick={handleClearLogs}>
              Limpar
            </Button>
          </div>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          <div className='relative'>
            <Search size={13} className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]' />
            <input
              type='text'
              placeholder='Buscar nos logs...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-md pl-8 pr-3 py-2 text-xs outline-none focus:border-accent transition-colors w-48'
            />
          </div>

          <div className='flex items-center gap-1 p-1 bg-white/[0.04] rounded-lg border border-white/[0.06]'>
            {logLevels.map((level) => (
              <button
                key={level}
                onClick={() => setLevelFilter(level)}
                className={cn(
                  'px-2.5 py-1 rounded-md text-[10px] font-bold uppercase transition-all min-h-[44px]',
                  levelFilter === level
                    ? level === 'all' ? 'bg-accent text-white' :
                      level === 'error' ? 'bg-red-500/20 text-red-400' :
                      level === 'warn' ? 'bg-yellow-500/20 text-yellow-400' :
                      level === 'debug' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-blue-500/20 text-blue-400'
                    : 'text-text-muted hover:text-text-primary',
                )}
              >
                {level === 'all' ? 'Todos' : level}
              </button>
            ))}
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as LogCategory | 'all')}
            className='bg-white/[0.04] border border-white/[0.06] text-text-primary rounded-md px-3 py-2 text-xs outline-none focus:border-accent transition-colors'
          >
            {logCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'Todas categorias' : categoryLabels[cat as LogCategory]}
              </option>
            ))}
          </select>

          <label className='flex items-center gap-1.5 text-[11px] text-[var(--font-color-muted)] cursor-pointer'>
            <input
              type='checkbox'
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className='w-3.5 h-3.5 accent-[var(--brand-primary)]'
            />
            Auto Refresh (15s)
          </label>
        </div>
      </div>

      <div className='glass-card p-0 overflow-hidden'>
        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <div className='w-5 h-5 border-2 border-[var(--brand-primary)]/30 border-t-[var(--brand-primary)] rounded-full animate-spin' />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className='text-center py-12'>
            <Terminal size={32} className='mx-auto text-[var(--font-color-muted)] mb-2 opacity-50' />
            <p className='text-sm text-[var(--font-color-muted)] italic'>Nenhum log encontrado.</p>
          </div>
        ) : (
          <div className='divide-y divide-[var(--glass-border)]'>
            {filteredLogs.map((log) => (
              <div key={log.id}>
                <button
                  onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  className='w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--glass-bg)] transition-colors text-left'
                >
                  <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold border flex items-center gap-1 whitespace-nowrap', levelConfig[log.level].color)}>
                    {levelConfig[log.level].icon}
                    {levelConfig[log.level].label}
                  </span>
                  <Badge variant='muted' className='text-[10px]'>
                    {categoryLabels[log.category]}
                  </Badge>
                  <span className='flex-1 text-[13px] text-[var(--font-color-body)] truncate'>
                    {log.message}
                  </span>
                  <span className='text-[11px] text-[var(--font-color-muted)] whitespace-nowrap flex items-center gap-1'>
                    <Terminal size={10} />
                    {formatDateTime(log.createdAt)}
                  </span>
                </button>
                {expandedId === log.id && log.details && (
                  <div className='px-4 pb-3'>
                    <pre className='text-[11px] text-[var(--font-color-muted)] bg-white/[0.02] p-3 rounded-lg overflow-x-auto font-mono border border-white/[0.06]'>
                      {log.details}
                    </pre>
                    {log.userName && (
                      <p className='text-[10px] text-[var(--font-color-muted)] mt-1'>
                        Usuário: {log.userName} {log.ip ? `· IP: ${log.ip}` : ''}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
